import { useEffect, useState, type MouseEvent } from "react";
import { useNavigate } from "react-router-dom";
import { BLOG_DATA_ID } from "@/lib/blog";

/**
 * The data the Worker embedded in this page, if it is for the page being
 * rendered. Only on the first render after a full page load: once the visitor
 * navigates in the app, the embedded data is the previous page's and `isFor`
 * rejects it.
 */
function embedded<T>(isFor: (data: unknown) => data is T): T | undefined {
  if (typeof document === "undefined") return undefined;
  const text = document.getElementById(BLOG_DATA_ID)?.textContent;
  if (!text) return undefined;
  try {
    const data: unknown = JSON.parse(text);
    return isFor(data) ? data : undefined;
  } catch {
    return undefined;
  }
}

/**
 * A blog page's data: what the Worker embedded on a full page load, otherwise
 * fetched from `url` (the Worker's /_blog JSON). `fallback` stands in when
 * the fetch fails outright, e.g. under `vite dev`, where there is no Worker.
 */
export function useBlogData<T>(url: string, isFor: (data: unknown) => data is T, fallback: T): T | undefined {
  const [data, setData] = useState<T | undefined>(() => embedded(isFor));
  useEffect(() => {
    if (data && isFor(data)) return;
    let live = true;
    setData(undefined);
    fetch(url, { headers: { Accept: "application/json" } })
      .then((res) => res.json())
      .then((body: unknown) => live && setData(isFor(body) ? body : fallback))
      .catch(() => live && setData(fallback));
    return () => {
      live = false;
    };
    // Refetch when the page changes, not when a new closure is passed in.
  }, [url]);
  return data && isFor(data) ? data : undefined;
}

/**
 * The part of a blog page the Worker fills in at the edge. It holds the same
 * HTML string the Worker wrote, from the same function, so hydration finds
 * nothing to change. Until there is any, a skeleton stands below it; the
 * Worker removes that skeleton from the pages it fills.
 *
 * Links in the article to the storefront itself navigate in the app rather
 * than reloading the page.
 */
export function BlogIsland({ html, className }: { html: string | undefined; className?: string }) {
  const navigate = useNavigate();
  const onClick = (e: MouseEvent<HTMLDivElement>) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const a = (e.target as HTMLElement).closest("a");
    const href = a?.getAttribute("href");
    if (!a || !href?.startsWith("/") || href.startsWith("//") || a.target) return;
    e.preventDefault();
    navigate(href);
  };
  return (
    <>
      <div data-blog-island="" className={className} onClick={onClick} dangerouslySetInnerHTML={{ __html: html ?? "" }} />
      {html === undefined && (
        <div data-blog-skeleton="" aria-hidden className="space-y-4">
          <div className="h-10 w-3/4 animate-pulse rounded bg-ink/8" />
          <div className="h-4 w-1/3 animate-pulse rounded bg-ink/8" />
          <div className="aspect-[16/9] w-full animate-pulse rounded-2xl bg-ink/8" />
          <div className="h-4 w-full animate-pulse rounded bg-ink/8" />
          <div className="h-4 w-5/6 animate-pulse rounded bg-ink/8" />
        </div>
      )}
    </>
  );
}
